/**
 * Central product & device naming for the onboarding flow.
 *
 * This is the single place to rename anything the pilot sees. Every screen
 * reads its product/device names from here, so a rebrand is a one-line edit.
 *
 * Vendor hardware names (AirThings, Wavin, Nilan, USR) live here too: the team
 * is still deciding whether to keep the vendor names or switch to proprietary
 * ones, and centralising them means that decision is a single change here
 * rather than a hunt across screens.
 *
 * (Kept as a typed TS module rather than YAML so it needs no extra Vite
 * loader/dependency and stays type-checked. Can migrate to YAML later if
 * non-developers need to edit names directly.)
 */
export const PRODUCT_NAMES = {
  // ─── Module names ──────────────────────────────────────────────────────
  // One canonical name per module, used for its guide card AND its app-bar
  // title, so the two can never drift apart.
  /** The Home Assistant hub, shown to pilots as the "SmartClimate Hub". */
  hub: "SmartClimate Hub",
  /**
   * The network switch shipped with the kit, for households whose router is
   * short on LAN ports (Figma 1150:984 labels it on the artwork).
   */
  hubSwitch: "Network switch",
  /** Air-quality module (Figma "AirQuality start" — was "AirSensors"). */
  airQuality: "Air Quality",
  /** Floor-heating module (Figma "Phase 4: Heating"). */
  heating: "Heating",
  /** Ventilation module (Figma "Phase 5: Ventilation"). */
  ventilation: "Ventilation",
  /**
   * Windows module. Carries the VELUX prefix on purpose: "Windows" alone reads
   * as a promise to control every window in the house, when the system only
   * ever drives VELUX io-homecontrol ones. The prefix scopes the expectation
   * and matches compartment 3's label on the kit box.
   */
  windows: "VELUX Windows",

  // ─── Connectors ────────────────────────────────────────────────────────
  // The four bridge devices. Every one of these names is still in flux, so
  // they're referenced (never inlined) across the module screens.
  //
  // All four follow one construction: descriptive domain + "Connector". Closed
  // compounds are reserved for coined product names ("SmartClimate"), which is
  // what the closed form signals. A connector is a description, not a coinage,
  // so "Air Quality Connector" takes two words like its three siblings. One
  // camel-cased name among three spaced ones reads as a typo, and camel case
  // mid-sentence is a speed bump when someone is reading with hardware in hand.
  /**
   * The AirThings hub as shown to pilots — it collects the sensors' readings
   * for SmartClimate (Figma "AirQuality start" 1056:1268).
   */
  airQualityConnector: "Air Quality Connector",
  /**
   * The Wavin USR gateway as shown to pilots — the bridge between the
   * floor-heating unit and the hub (Figma "Phase 4: Heating").
   */
  heatingConnector: "Heating Connector",
  /**
   * The Nilan USR gateway as shown to pilots — the bridge between the
   * ventilation unit and the hub (Figma "Phase 5: Ventilation").
   */
  ventilationConnector: "Ventilation Connector",
  /**
   * The KLG 300 gateway as shown to pilots — the bridge that carries the
   * windows from the VELUX Touch into the hub.
   */
  windowConnector: "VELUX Window Connector",

  /**
   * The VELUX Touch remote (KLR family). Never shortened in copy — see the
   * naming rule in .claude/rules/general.md. Interpolate this rather than
   * hand-typing the name.
   */
  touchRemote: "VELUX Touch",

  /**
   * Vendor hardware names as printed on the physical devices/box. Swap these
   * to proprietary names in one edit once the naming decision lands.
   */
  vendor: {
    airSensors: "AirThings",
    floorHeating: "Wavin",
    ventilation: "Nilan",
    gateway: "USR gateway",
  },
} as const;

/**
 * Module display name per flow step key — the guide cards and each module's
 * app bar both read from here, so renaming a module is one edit above.
 */
export const MODULE_NAMES: Record<string, string> = {
  home_assistant: PRODUCT_NAMES.hub,
  air_sensors: PRODUCT_NAMES.airQuality,
  floor_heating: PRODUCT_NAMES.heating,
  ventilation: PRODUCT_NAMES.ventilation,
  windows: PRODUCT_NAMES.windows,
};
